import React, { useState } from 'react';
import { message } from 'antd';
import { useCompany } from '../../contexts/CompanyContext';
import { useSecureRequest } from '../../hooks/useSecureRequest';
import {
  useJournalEntries,
  usePendingAccountingMovements,
  useAccounts,
  usePeriodLock,
  useContacts,
} from '../../hooks/useAccounting';
import { JournalEntry } from '../../services/accountingService';
import dayjs from 'dayjs';
import {
  JournalEntriesHeaderBar,
  JournalEntriesTable,
  JournalEntryDetailDrawer,
  PendingMovementsModal,
  CreateJournalEntryModal,
} from './journal-entries';

const JournalEntriesView: React.FC = () => {
  const { company } = useCompany();
  const { securePost, loading } = useSecureRequest();

  const [dates, setDates] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().startOf('month'),
    dayjs().endOf('month'),
  ]);

  // SWR Reactive Data Hooks
  const {
    entries,
    isLoading: entriesLoading,
    isValidating: entriesValidating,
    mutate: mutateEntries,
  } = useJournalEntries({
    company_id: company.current.id,
    start_date: dates[0]?.format('YYYY-MM-DD'),
    end_date: dates[1]?.format('YYYY-MM-DD'),
  });

  const {
    accounts,
    mutate: mutateAccounts,
  } = useAccounts({
    company_id: company.current.id,
  });

  const {
    contacts,
    mutate: mutateContacts,
  } = useContacts({
    company_id: company.current.id,
    company_uid: company.current.uid || company.current.name,
  });

  const {
    lockDate,
    mutate: mutateLock,
  } = usePeriodLock({
    company_id: company.current.id,
  });

  const {
    pendingMovements,
    isLoading: pendingLoading,
    isValidating: pendingValidating,
    mutate: mutatePending,
  } = usePendingAccountingMovements({
    company_id: company.current.id,
  });

  // Drawer details state
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Pending / Failed Movements state
  const [pendingModalVisible, setPendingModalVisible] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  // New Entry modal state
  const [modalVisible, setModalVisible] = useState(false);

  const refreshAllData = async () => {
    await Promise.all([
      mutateEntries(),
      mutatePending(),
      mutateAccounts(),
      mutateLock(),
      mutateContacts(),
    ]);
  };

  const handleReprocessPending = async () => {
    if (!company.current.id) return;
    try {
      setReprocessing(true);
      const res = await securePost('/accounting/reprocess-pending', {
        company_id: company.current.id,
      });
      const data = res?.data || res;
      if (data?.processed > 0) {
        message.success(`Se procesaron ${data.processed} movimiento(s) exitosamente.`);
      } else if (data?.failed > 0) {
        message.warning(
          `No se pudieron contabilizar ${data.failed} movimiento(s). Verifique si la fecha cae en periodo cerrado.`
        );
      } else {
        message.info('No hay movimientos pendientes de contabilizar.');
      }
      await mutateEntries();
      await mutatePending();
    } catch (err: any) {
      console.error('Error reprocessing movements:', err);
      message.error(err?.message || 'Error al reprocesar movimientos.');
    } finally {
      setReprocessing(false);
    }
  };

  const handleProcessSingle = async (r: any, targetDate?: string) => {
    try {
      const payload: any = {
        documentId: r.id,
        company_id: company.current.id,
      };
      if (targetDate) {
        payload.entry_date = targetDate;
      }
      const res = await securePost('/accounting/process-movement', payload);
      const success = res?.data?.success || res?.success;
      if (success) {
        message.success(
          targetDate
            ? `Documento #${r.document_number || r.id} contabilizado con fecha ${dayjs(
                targetDate
              ).format('DD/MM/YYYY')}.`
            : `Documento #${r.document_number || r.id} contabilizado con éxito.`
        );
        await mutateEntries();
        await mutatePending();
      } else {
        message.warning(res?.data?.message || res?.message || 'No se pudo contabilizar.');
      }
    } catch (err: any) {
      message.error(err?.message || 'Error al procesar movimiento.');
    }
  };

  const handleOpenDetail = async (record: JournalEntry) => {
    setSelectedEntry(record);
    setDrawerVisible(true);
    setDrawerLoading(true);
    try {
      const res = await securePost('/accounting/get-entry', {
        id: record.id,
        company_id: company.current.id,
      });
      if (res?.data) {
        const raw = res.data?.entry || res.data;
        const rawLines = raw.items || raw.lines || raw.accounting_entry_items || [];
        const mappedLines = rawLines.map((l: any) => ({
          ...l,
          id: l.id,
          debit: Number(l.debit || 0),
          credit: Number(l.credit || 0),
          account_code: l.account_code || l.account?.code || '',
          account_name: l.account_name || l.account?.name || '',
          contact_name: l.contact_name || l.contacts?.name || '',
          description: l.description || '',
        }));
        setSelectedEntry({
          ...record,
          ...raw,
          items: mappedLines,
          lines: mappedLines,
          entry_number: raw.entry_number || record.entry_number,
          entry_date: raw.entry_date || record.entry_date,
          concept: raw.concept || record.concept,
        });
      }
    } catch (err) {
      console.error('Error loading entry detail:', err);
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleSaveEntry = async (entryPayload: {
    date: string;
    description: string;
    is_adjustment: boolean;
    lines: Array<{
      account_id: number;
      contact_id: number | null;
      description: string;
      debit: number;
      credit: number;
    }>;
  }) => {
    try {
      const payload = {
        company_id: company.current.id,
        ...entryPayload,
      };

      await securePost('/accounting/journal-entries', payload);
      message.success('Comprobante contable registrado con éxito.');
      setModalVisible(false);
      await mutateEntries();
    } catch (err: any) {
      console.error('Error creating journal entry:', err);
      message.error(err?.message || 'Error al guardar el comprobante contable.');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header */}
      <JournalEntriesHeaderBar
        dates={dates}
        onDatesChange={setDates}
        lockDate={lockDate}
        isRefreshing={entriesValidating || pendingValidating || loading}
        onRefresh={refreshAllData}
        pendingCount={pendingMovements.length}
        isReprocessing={pendingValidating || pendingLoading || reprocessing}
        onOpenPendingModal={() => {
          mutatePending();
          setPendingModalVisible(true);
        }}
        onOpenCreateModal={() => setModalVisible(true)}
      />

      {/* Entries Table */}
      <JournalEntriesTable
        entries={entries}
        loading={entriesLoading || entriesValidating}
        onOpenDetail={handleOpenDetail}
      />

      {/* Detail Drawer */}
      <JournalEntryDetailDrawer
        entry={selectedEntry}
        visible={drawerVisible}
        loading={drawerLoading}
        onClose={() => setDrawerVisible(false)}
      />

      {/* Create Manual Entry Modal */}
      <CreateJournalEntryModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSave={handleSaveEntry}
        accounts={accounts}
        contacts={contacts}
        lockDate={lockDate}
        loading={loading}
      />

      {/* Pending / Failed Movements Modal */}
      <PendingMovementsModal
        visible={pendingModalVisible}
        onClose={() => setPendingModalVisible(false)}
        pendingMovements={pendingMovements}
        loading={pendingLoading}
        reprocessing={reprocessing}
        lockDate={lockDate}
        onReprocessAll={handleReprocessPending}
        onProcessSingle={handleProcessSingle}
      />
    </div>
  );
};

export default JournalEntriesView;
